<template>
	<div
		class="webmd-container fluid"
		v-if="providers.length && !sponsoredPage"
		:style="{ visibility: fallbackLocation ? undefined : 'hidden' }"
	>
		<div class="webmd-container">
			<div class="head-wrapper">
				<h3>
					Top doctors in
					{{ fallbackLocation ? fallbackLocation.city : '' }},
					{{ fallbackLocation ? fallbackLocation.state : '' }}
				</h3>
				<div
					class="sp-logo-wrapper"
					v-if="providers.filter((p) => p !== null).length"
				>
					<span
						v-if="campaignData &&
							campaignData.CampaignLogoUrl &&
							moduleType === MODULE_TYPE.CAMPAIGN
						"
					>
						From our sponsor
					</span>
					<span
						v-else-if="(campaignData &&
							moduleType &&
							moduleType === MODULE_TYPE.NO_CAMPAIGN_ENHANCED)
							|| (moduleType === MODULE_TYPE.NO_CAMPAIGN && anySpEPs)
						"
					>
						Sponsored Results
					</span>
					<img
						class="sp-logo"
						v-if="campaignData && campaignData.CampaignLogoUrl &&
							moduleType &&
							moduleType !== (MODULE_TYPE.NO_CAMPAIGN
								|| MODULE_TYPE.NO_CAMPAIGN_ENHANCED)
						"
						:alt="campaignData.CampaignText || undefined"
						:title="campaignData.CampaignText || undefined"
						:src="campaignData.CampaignLogoUrl"
					/>
				</div>
			</div>
		</div>
		<carousel
			:providers="providers"
			:campaign-data="campaignData"
		>
			<search-bar
				:fallback-location="fallbackLocation"
				:campaign-data="campaignData"
			/>
		</carousel>
		<dummy-rate />
	</div>
</template>
<script>
import useStore from 'webmd-cms-store/src/store';
import Carousel from './components/Carousel.vue';
import SearchBar from './components/SearchBar.vue';
import DummyRate from './components/DummyRate.vue';
import { getFallbackLocation } from './components/utils.es6';
import { getCareFinderProviders } from './components/services.es6';

export default {
	created() {
		this.store = useStore(this.$pinia);
	},

	name: 'TravellingModule',
	emits: ['no-providers'],
	props: {
		topicId: {
			type: String,
			required: false,
			default: '',
		},
		location: {
			type: Object,
			required: false,
			default: () => {},
		},
	},
	data: () => ({
		providers: [null, null, null],
		campaignData: null,
		fallbackLocation: null,
		isInViewport: false,
		observer: null,
		moduleType: null,
		MODULE_TYPE: {
			NO_CAMPAIGN: 'no_campaign',
			NO_CAMPAIGN_ENHANCED: 'no_campaign_enhanced',
			CAMPAIGN: 'campaign',
		},
		sponsoredPage: false,
	}),
	components: {
		Carousel,
		SearchBar,
		DummyRate,
	},
	computed: {
		anySpEPs() {
			const basicProviders = this.providers.filter((provider) => {
				if (provider) {
					return provider.profiletype_s === 'Basic';
				}
				return false;
			});
			return basicProviders.length !== 3;
		},
	},
	async mounted() {
		const state = this.store;
		const topicId = this.topicId || state?.pagedata?.omniture?.s_topic || '1625';
		const capId = state?.pagedata?.omniture?.s_cap_id || '';

		if (typeof window !== 'undefined') {
			try {
				await this.fetchFallbackLocation();
				await this.fetchProviders(topicId, capId);
				this.observer = new IntersectionObserver(this.handleIntersection, {
					root: null,
					threshold: 0.6,
				});
				this.observer.observe(this.$el);
				this.sponsoredPage = this.isSponsoredPage();
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log('CareFinder API error', e);
			}
		}
	},
	methods: {
		isSponsoredPage() {
			return this.store.pagedata?.omniture?.s_sponsor_program ||
				this.store.pagedata?.omniture?.context?.s_sponsor_program;
		},
		handleIntersection(entries) {
			const [entry] = entries;
			this.isInViewport = entry.isIntersecting;
			if (this.isInViewport) {
				this.observer.disconnect();
				this.setOmniture(this.store || {}, null);
			}
		},
		setOmniture() {
			if (this.campaignData) {
				const { campaignId } = this.campaignData;
				let pdicdimp = '';
				let dimp = '';

				switch (this.moduleType) {
					case this.MODULE_TYPE.CAMPAIGN:
						dimp = campaignId;
						break;
					case this.MODULE_TYPE.NO_CAMPAIGN:
						if (!this.anySpEPs) {
							dimp = `basic-${this.providers.map(
								(p) => `${p.providerid}`,
							).join(':')}`;
						} else {
							// no_campaign requires appropriate prefix dimp before each provider Id when there are mixed profile types
							dimp = `${this.providers.map(
								(p) => `${p.profiletype_s === 'Basic' ? 'basic-' : 'e-'}${p.providerid}`,
							).join(':')}`;
						}
						break;
					case this.MODULE_TYPE.NO_CAMPAIGN_ENHANCED:
						dimp = `e-${this.providers.map(
							(p) => `${p.providerid}`,
						).join(':')}`;
						break;
					default:
						break;
				}
				pdicdimp = `pd-trav1-imp_${dimp}`;

				this.$vwmdPageLink(
					{ moduleName: 'pd-trav1-imp' },
					{ metrics: { 'wb.pdicdimp': pdicdimp } },
				);
			}
		},
		async fetchProviders(topicId, capId) {
			try {
				const providersResp = await getCareFinderProviders({
					topicid: topicId,
					capid: capId,
					lat: this.fallbackLocation.lat,
					lon: this.fallbackLocation.lon,
				});
				if (
					providersResp.code === 0
					&& providersResp.data?.Providers?.length
				) {
					this.campaignData = providersResp.data;
					this.providers = this.campaignData.Providers;
					this.moduleType = this.campaignData.moduleType;

					/*
					 * API will return moduleType as 'campaign' or 'no_campaign'
					 * when no_campaign is returned we pass 'e-' instead of 'basic-'
					 * if all 3 providers are enhanced
					 */
					if (this.moduleType === this.MODULE_TYPE.NO_CAMPAIGN) {
						const enhancedProvidersPresent = Object.values(this.providers)
							.filter((p) => p.profiletype_s === 'Enhanced');
						if (enhancedProvidersPresent.length !== 3) {
							this.moduleType = this.MODULE_TYPE.NO_CAMPAIGN;
						} else {
							this.moduleType = this.MODULE_TYPE.NO_CAMPAIGN_ENHANCED;
						}
					}
				} else {
					this.$emit('no-providers');
					this.providers = [];
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log('CareFinder fetch provider error', e);
			}
		},
		async fetchFallbackLocation() {
			try {
				let fallback = this.location;
				if (!fallback) {
					const response = await getFallbackLocation();
					fallback = response?.data?.data || {};
				}
				fallback.Latitude = fallback.Latitude || fallback.lat || '';
				fallback.Longitude = fallback.Longitude || fallback.lon || '';
				fallback.City = fallback.City || fallback.ct || '';
				fallback.Zip = fallback.Zip || fallback.z || '';
				fallback.State = fallback.State || fallback.st || '';

				let str = fallback.City.length > 0 ? `${fallback.City}, ` : '';
				str += fallback.State.length > 0 ? `${fallback.State} ` : '';
				str += fallback.Zip.length > 0 ? fallback.Zip : '';
				this.fallbackLocation = {
					type: 'text',
					styles: '',
					link: '',
					lat: fallback.Latitude,
					lon: fallback.Longitude,
					name: str,
					value: str,
					show: str,
					city: fallback.City,
					state: fallback.State,
					zip: fallback.Zip,
				};
				if (!document.cookie.match('gtinfo={(.*?)}')?.[0]) {
					document.cookie = `gtinfo=${JSON.stringify({
						Latitude: this.fallbackLocation.lat,
						Longitude: this.fallbackLocation.lon,
						City: this.fallbackLocation.city,
						State: this.fallbackLocation.state,
						Zip: this.fallbackLocation.zip,
					})}`;
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log('CareFinder location fetch error', e);
				this.fallbackLocation = {
					type: 'text',
					styles: '',
					link: '',
					lat: '40.7396',
					lon: '-74.0007',
					name: 'New York, NY 10011',
					value: 'New York, NY 10011',
					show: 'New York, NY 10011',
					city: 'New York',
					state: 'NY',
					zip: '10011',
				};
			}
		},
	},
};
</script>
<style>
	:root {
		--care-bg-lightgray: #F8F8F8;
		--care-bg-darknavy: #000625;
		--care-text-black: #1B1B1B;
		--care-text-white: #FFFFFF;
		--care-text-darkblue: #00157c;
		--care-text-lightblue: #3557ff;
		--care-icon-lightviolet: #7384D8;
	}
</style>
<style lang="scss" scoped>
* {
	margin: 0;
	padding: 0;
	box-sizing: border-box;
}
.webmd-container {
	display: block;
	font-family: 'Source Sans Pro', sans-serif;

	&.fluid {
		padding: 50px 0;
		margin: 42px 0;
		max-width: 100%;
		background: var(--care-bg-lightgray);
		/* Old browsers */
		background: -moz-linear-gradient(top,
				var(--care-bg-lightgray) 0%,
				var(--care-bg-lightgray) 57%,
				var(--care-bg-darknavy) 57%,
				var(--care-bg-darknavy) 100%);
		/* FF3.6-15 */
		background: -webkit-linear-gradient(top,
				var(--care-bg-lightgray) 0%,
				var(--care-bg-lightgray) 57%,
				var(--care-bg-darknavy) 57%,
				var(--care-bg-darknavy) 100%);
		/* Chrome10-25,Safari5.1-6 */
		background: linear-gradient(to bottom,
				var(--care-bg-lightgray) 0%,
				var(--care-bg-lightgray) 57%,
				var(--care-bg-darknavy) 57%,
				var(--care-bg-darknavy) 100%);
		/* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
		filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#F8F8F8',
				endColorstr='#000625',
				GradientType=0);
		/* IE6-9 */
	}

	&:not(.fluid) {
		max-width: 1232px;
		margin-left: auto;
		margin-right: auto;

		@media screen and (max-width: 1280px) {
			padding-left: 30px;
			padding-right: 30px;
		}

		@media screen and (max-width: 767px) {
			padding-left: 20px;
			padding-right: 20px;
		}
	}

	.head-wrapper {
		display: flex;
		justify-content: space-between;
		width: 100%;
		align-items: flex-end;

		@media screen and (max-width: 1023px) {
			align-items: center;
		}

		.sp-logo-wrapper {
			display: flex;
			justify-content: center;
			align-items: center;
			font-weight: 400;
			font-size: 12px;
			line-height: 14px;
			color: var(--care-text-black);
			min-height: 34px;
			font-family: 'Roboto', sans-serif;

			@media screen and (max-width: 767px) {
				flex-direction: column;
				min-width: 100px;
				align-items: flex-end;
			}

			.sp-logo {
				max-height: 34px;
				display: inline-block;
				margin-left: 1rem;

				@media screen and (max-width: 767px) {
					margin-left: 0;
					margin-top: 5px;
				}
			}
		}

		h3 {
			font-weight: 600;
			font-size: 26px;
			line-height: 33px;
			color: var(--care-text-darkblue);
			margin: 0;

			@media screen and (max-width: 767px) {
				font-size: 20px;
				line-height: 25px;
				word-break: break-word;
			}
		}
	}
}
</style>
